import { alias, readOnly } from '@ember/object/computed';
import EmberObject, { computed } from '@ember/object';
import Service from '@ember/service';
import { initializePropertyDefaults } from 'sh-www/utils/initialize-state-properties';
import { ASSIGNMENT } from "../../../models/assignment";
import { v1 } from "ember-uuid";
import { POSTINGS_NETWORK_FILTER_API_ENUMS } from "../../../constants/routes/postings";
import { ICON_SORTS } from "../../../constants/lsg";

export default Service.extend({
  /**
   * Setup default state here
   */
  propertyDefaults: {
    /**
     * Will be set to the ID of the assignment that was clicked on.  An observer should watch this and make
     * the transition.
     *
     * @type {Number|null}
     */
    assignmentIDClickedOn: null,

    /**
     * Main model for the view based on the user search, sorts, and filters
     *
     * @type {AssignmentDashboardList|null}
     */
    assignmentDashboardList: null,

    /**
     * We'll use this to store the results from the primary assignmentDashboardList while we're searching.
     * When search is (de)activated, we will swap this cache with the primary list which will allow all
     * the downstream state to be set up correctly.
     *
     * @type {AssignmentDashboardList|null}
     */
    assignmentDashboardListWhileSearchingCache: null,

    /**
     * The current assignment status we're viewing.
     *
     * @type {String}   of type Assignment.STATUS_*
     */
    currentAssignmentStatus: ASSIGNMENT.STATUS.LIVE,

    /**
     * When this cache key changes, we'll know our current client side cache has become invalid and
     * should be reset.
     *
     * This should be random key that we'll generate for each set of search results.  This should stay
     * the same during paging but should change any other time.
     *
     * @type {String}   The current cache key of the postings list.
     */
    currentCacheKey: () => v1(),

    /**
     * The selected value of the company team filter dropdown.
     *
     * @type {Number|null}   of type CompanyTeam.id
     */
    companyTeamFilterSelectedValue: null,

    /**
     * The selected value of the filter dropdown.
     *
     * @type {String}   of type POSTINGS_NETWORK_FILTER_API_ENUMS.*
     */
    networkFilterSelectedValue: POSTINGS_NETWORK_FILTER_API_ENUMS.POSTED_BY_ANYONE,

    /**
     * A map of the page number => AssignmentDashboardList Objects.  When the cache key changes,
     * this cache will be reset.
     *
     * @type {Object}
     */
    pageToResultsMap: () => EmberObject.create({}),

    /**
     * Will be set to true when we're currently fetching a result.
     *
     * @type {Boolean}
     */
    isLoadingResult: false,

    /**
     * True when the search component is activated
     *
     * @type {Boolean}
     * @private
     */
    isSearchActivated: false,

    /**
     * The secondary sort for our state.
     *
     * @type {String}   of type ICON_SORTS.*
     */
    secondarySort: ICON_SORTS.SORT_DESCENDING
  },

  /**
   * The list items that will represent all of the assignments that we show the user for any given
   * search or filter
   *
   * @type {Ember.MutableArray}
   */
  assignmentListItems: alias('assignmentDashboardList.assignmentSummaries'),

  /**
   * The current pagination model
   *
   * @type {Pagination|null}
   */
  currentPagination: readOnly('assignmentDashboardList.pagination'),

  /**
   * @type {Boolean}    True when we have NOT searched yet.
   */
  hasNotSearchedYet: computed('assignmentDashboardList', function() {
    return this.get('assignmentDashboardList') == null;
  }),

  /**
   * @type {Boolean}    True when the first search on the page has an empty set.
   */
  isEmptyDefaultSearch: computed('isFirstQuery', 'assignmentListItems.length', function() {
    return !this.get('assignmentListItems.length') && this.get('isFirstQuery');
  }),

  /**
   * This is the first query run on a 'page' load.  e.g. a tab click from 'live' => 'draft' would need be a 'firstQuery',
   * but updating any sorts or filters would NOT be a `firstQuery`
   *
   * @type {Boolean}    True when the ONLY query that has been run is the first for the page.
   */
  isFirstQuery: true,

  /**
   * Will be set to true when our state is being reset in between transitions.  This is typically
   * useful for aborting synchronous observers as we reset their values.
   *
   * @type {Boolean}
   */
  isResettingState: false,


  initializeDefaults() {
    initializePropertyDefaults(this);
  },

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);
    this.initializeDefaults();
  }
});
